import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, AtSign, MessagesSquare, Ticket, CheckCheck } from "lucide-react";
import { fmtDateTime } from "@/components/shared";

const ICON = { mention: AtSign, new_conversation: MessagesSquare, ticket_reply: Ticket };

export function NotificationBell({ slug }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const timer = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data.notifications); setUnread(data.unread); } catch {}
  };
  useEffect(() => { load(); timer.current = setInterval(load, 30000); return () => clearInterval(timer.current); }, []);

  const openItem = async (n) => {
    if (!n.read) { api.post(`/notifications/${n.id}/read`).catch(() => {}); setUnread((u) => Math.max(0, u - 1)); setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x)); }
    setOpen(false);
    if (n.conversation_id) navigate(`/${slug}/comms?c=${n.conversation_id}`);
    else if (n.ticket_id) navigate(`/${slug}/tickets`);
    else if (n.link) navigate(n.link);
  };

  const markAll = async () => { await api.post("/notifications/read-all"); setUnread(0); setItems((prev) => prev.map((x) => ({ ...x, read: true }))); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative h-9 w-9 rounded-full border border-[#EEEFEF] bg-white hover:bg-[#F6F6FD] flex items-center justify-center transition-colors">
          <Bell className="h-[18px] w-[18px] text-[#595D62]" />
          {unread > 0 && <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#723FFF] text-white text-[10px] font-semibold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-white border-[#EEEFEF] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#f0f0f3]">
          <span className="font-display font-semibold text-sm text-[#06060C]">Notifications</span>
          {unread > 0 && <button data-testid="notification-mark-all" onClick={markAll} className="flex items-center gap-1 text-xs text-[#723FFF] hover:text-[#5b2fd6]"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>}
        </div>
        <div className="flex items-center gap-1 px-3 py-2 border-b border-[#f6f6fa]">
          <button data-testid="notification-filter-all" onClick={() => setFilter("all")} className={`px-2.5 py-1 rounded-full text-xs font-medium ${filter === "all" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>All</button>
          <button data-testid="notification-filter-mention" onClick={() => setFilter("mention")} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${filter === "mention" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><AtSign className="h-3 w-3" /> Mentions</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {(() => { const shown = filter === "mention" ? items.filter((n) => n.type === "mention") : items; return shown.length === 0 ? (
            <p className="text-sm text-[#9FA1A4] text-center py-10">{filter === "mention" ? "No mentions yet." : "You're all caught up."}</p>
          ) : shown.map((n) => {
            const Icon = ICON[n.type] || Bell;
            return (
              <button key={n.id} data-testid="notification-item" onClick={() => openItem(n)} className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[#f6f6fa] hover:bg-[#F6F6FD] transition-colors ${n.read ? "" : "bg-[#F3F0FF]"}`}>
                <span className="h-8 w-8 rounded-xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-[#06060C] font-medium truncate">{n.title}</span>
                  {n.body && <span className="block text-xs text-[#9FA1A4] truncate">{n.body}</span>}
                  <span className="block text-[10px] text-[#c4c5cc] mt-0.5">{fmtDateTime(n.created_at)}</span>
                </span>
                {!n.read && <span className="dot mt-1.5" style={{ backgroundColor: "#723FFF" }} />}
              </button>
            );
          }); })()}
        </div>
      </PopoverContent>
    </Popover>
  );
}
